import * as React from 'react';
import * as _ from 'lodash'
import * as moment from 'moment'
import { Button, ButtonType } from 'office-ui-fabric-react';
import Progress from '../views/Progress';
import Message from '../views/Message';
import CampaignStart from '../views/CampaignStart';
import CampaignView from '../views/CampaignView';
import {
    clientLog,
    apiUnlink,
    apiCreateCampaign,
    apiDeleteCampaign,
    apiGetCampaign,
    apiSetCampaignSettings,
    apiUploadSourceData,
    apiUploadSourceDataDirect,
    apiContinueSourceData,
    apiSetCampaignPreview,
    apiCheckSourceData
} from '../util'
import {
    EmailTypeService,
    OfficeAsyncError,
    isStaleItemError,
    isPreviousCallInProgressError,
    MSG_MAILTYPE_TRANSIENT,
    MSG_MAILTYPE_FAILED,
    MSG_MAILTYPE_ERROR
} from '../emailTypeService'

export interface Props {
    firstRun: boolean;
    title: string;
    exchangeItemID: string;
    account: any;
    autoLogin: (any, string) => string;
    onSignout: () => void,
}

export interface State {
    hasSentPreviousCampaign: boolean;
    loaded: boolean;
    working: boolean;
    newCampaign: boolean;
    campaign: any;
    error: string;
    errorAdditional: string;
    files: FileList;
    directData: string;
    errorDismissable: boolean;
    mailType: Office.CoercionType;
    attachmentWarning: boolean;
    warning: string;
    warningCancelAction: () => void;
    warningContinueAction: () => void;
}

export default class Campaign extends React.Component<Props, State> {

    // Serializes all body.getTypeAsync calls: single-flight, stale-result
    // detection via generations, bounded retry of error 5100
    emailTypeService = new EmailTypeService(() => Office.context.mailbox.item);
    recipientPollHandle: any = null;
    recipientCheckInFlight = false;
    recipientCheckStartedAt = 0;
    consecutiveTypeFailures = 0;
    isUnmounted = false;

    constructor(props, context) {
        super(props, context);
        this.state = this.emptyState();
    }

    emptyState = () => ({
        hasSentPreviousCampaign: false,
        loaded: false,
        campaign: null,
        newCampaign: false,
        error: null,
        errorAdditional: null,
        errorDismissable: false,
        files: null,
        directData: null,
        mailType: null,
        attachmentWarning: false,
        working: false,
        warning: null,
        warningCancelAction: null,
        warningContinueAction: null,
    })

    async componentDidMount() {
        await this.getCampaign();
        await this.getItemType('startup');

        // startPolling registers the interval exactly once, even if mounting
        // logic ever runs twice
        this.emailTypeService.startPolling(3000, () => this.getItemType('poll'));
        this.recipientPollHandle = setInterval(() => this.checkCampaignRecipient(), 2500);
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.exchangeItemID !== this.props.exchangeItemID) {
            // The task pane now belongs to a different Outlook item: mark any
            // pending detection as stale and re-detect for the new item once
            // the in-flight call has settled
            this.emailTypeService.newGeneration('item-changed');
            this.setState({ mailType: null });
            this.getItemType('item-changed');
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        this.emailTypeService.dispose();
        if (this.recipientPollHandle != null) {
            clearInterval(this.recipientPollHandle);
            this.recipientPollHandle = null;
        }
    }

    getItemType = async (trigger: string) => {
        if (this.state.working) {
            // The campaign creation flow issues its own Office.js item calls;
            // do not compete with them for the host's concurrent-call limit
            return;
        }

        try {
            const result = await this.emailTypeService.getEmailType(trigger);

            if (this.isUnmounted || result.generation !== this.emailTypeService.currentGeneration) {
                return; // stale result from a previous item; never reaches the UI
            }

            this.consecutiveTypeFailures = 0;

            const updates: any = {};
            if (this.state.mailType !== result.mailType) {
                clientLog('Mail type has changed from ' + this.state.mailType + ' to ' + result.mailType);
                updates.mailType = result.mailType;
            }
            // Detection recovered: clear our own transient/failure message
            if (this.state.error === MSG_MAILTYPE_TRANSIENT || this.state.error === MSG_MAILTYPE_FAILED) {
                updates.error = null;
                updates.errorAdditional = null;
                updates.errorDismissable = false;
            }
            if (Object.keys(updates).length > 0) {
                this.setState(updates);
            }
        } catch (err) {
            if (this.isUnmounted || isStaleItemError(err)) {
                return;
            }

            const code = err && (err as OfficeAsyncError).code;
            clientLog(`Error in getItemType: ${err && err.message} (code: ${code})`);

            // Only surface the problem while the mail type is still unknown;
            // with a known type a transient poll failure is log-only
            if (this.state.mailType == null) {
                this.consecutiveTypeFailures++;
                const technical = `body.getTypeAsync failed: ${err && err.message} (code: ${code})`;
                if (isPreviousCallInProgressError(err)) {
                    this.setState({
                        error: this.consecutiveTypeFailures >= 3 ? MSG_MAILTYPE_FAILED : MSG_MAILTYPE_TRANSIENT,
                        errorAdditional: technical,
                        errorDismissable: true,
                    });
                } else {
                    this.setState({
                        error: MSG_MAILTYPE_ERROR,
                        errorAdditional: technical,
                        errorDismissable: true,
                    });
                }
            }
        }
    }


    getCampaign = async () => {
        let campaignResult = await apiGetCampaign(this.props.exchangeItemID);
        if (campaignResult.success) {
            clientLog(`getCampaign: OK - ${campaignResult.campaign && campaignResult.campaign.campaignID}`);
            this.setState({ hasSentPreviousCampaign: campaignResult.hasSentPreviousCampaign });
            this.loadCampaign(campaignResult.campaign, false);

        } else {
            clientLog(`Error in getCampaign: ${campaignResult.reason}`);
            this.setState({ error: campaignResult.reason })

        }
    }

    createCampaign = async (cloneBodyAndSubjectFromCampaignID?: string, createSourceFromCampaignID?: string, createSourceFromCriteria?: string) => {
        this.setState({ working: true });

        let campaignResult = await apiCreateCampaign(
            this.props.exchangeItemID,
            null,
            cloneBodyAndSubjectFromCampaignID,
            createSourceFromCampaignID,
            createSourceFromCriteria);

        if (campaignResult.success) {
            clientLog(`createCampaign: OK - ${campaignResult.campaign && campaignResult.campaign.campaignID}`);
            this.setState({ hasSentPreviousCampaign: campaignResult.hasSentPreviousCampaign });

            console.log(campaignResult.campaign);

            this.loadCampaign(campaignResult.campaign, true)

            await this.onSetSettings({
                readTrackingEnabled: null,
                linkTrackingEnabled: null,
                unsubscribeEnabled: null,
                bcc: campaignResult.campaign.bcc,
                replyTo: campaignResult.campaign.replyTo,
                delayInSecondsForEachCampaignMail: campaignResult.campaign.delayInSecondsForEachCampaignMail
            });

            const {
                mailType
            } = this.state;

            const item = Office.context.mailbox.item;
            if (campaignResult.subject && campaignResult.subject.length > 0) {
                item.subject.getAsync((exisingSubject) => {
                    if (!exisingSubject || !exisingSubject.value || exisingSubject.value.length == 0) {
                        item.subject.setAsync(
                            campaignResult.subject,
                            {
                                asyncContext: {}
                            },
                            (asyncResult) => {
                                if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                                    clientLog("Error in createCampaign-setSubject: " + asyncResult.error.message)
                                }
                            });
                    }
                });
            }

            campaignResult.attachments.forEach(obj => {
                if (obj.contentID && obj.contentID.length > 0 && obj.name && obj.name.length > 0 && obj.name.indexOf(".") >= 0) {
                    var nameParts = obj.name.split(".");
                    var extension = nameParts[nameParts.length - 1];

                    var oldContentID = obj.contentID;
                    obj.contentID = obj.contentID + "." + extension;
                    var reg = new RegExp('cid:' + oldContentID, 'gi');

                    if (campaignResult.htmlBody && campaignResult.htmlBody.length > 0) {
                        campaignResult.htmlBody = campaignResult.htmlBody.replace(reg, "cid:" + obj.contentID);
                    }
                    if (campaignResult.textBody && campaignResult.textBody.length > 0) {
                        campaignResult.textBody = campaignResult.textBody.replace(reg, "cid:" + obj.contentID);
                    }
                }
            });

            var loadContents = () => {
                if (mailType == Office.CoercionType.Html) {
                    if (campaignResult.htmlBody && campaignResult.htmlBody.length > 0) {
                        item.body.getAsync(Office.CoercionType.Html, (existingBodyResult) => {

                            var existingBodyValue = (existingBodyResult.value || '').trim();

                            if (existingBodyValue.endsWith("<br>")) {
                                existingBodyValue = existingBodyValue.substring(0, existingBodyValue.length - "<br>".length);
                            }

                            existingBodyValue = existingBodyValue.substring(Math.max(0, existingBodyValue.toLowerCase().indexOf("<body")));
                            console.log('existing body1:' + existingBodyValue + '|')

                            existingBodyValue = existingBodyValue.substring(Math.max(0, existingBodyValue.toLowerCase().indexOf(">")+1));
                            console.log('existing body2:' + existingBodyValue + '|')

                            if (existingBodyValue.toLowerCase().indexOf("</body>") >= 0) {
                                existingBodyValue = existingBodyValue.substring(0, existingBodyValue.toLowerCase().indexOf("</body>"));
                                console.log('existing body3:' + existingBodyValue + '|')
                            }

                            console.log('existing body:' + existingBodyValue + '|')

                            var newBody = existingBodyValue;
                            if (newBody.toLowerCase().indexOf('</body>') >= 0) {
                                var searchMask = "</body>";
                                var regEx = new RegExp(searchMask, "ig");
                                var replaceMask = existingBodyValue+ "</body>";

                                newBody = newBody.replace(regEx, replaceMask);
                            } else {
                                newBody = campaignResult.htmlBody;
                            }

                            console.log('campaign body:' + newBody + '|')

                            item.body.setAsync(
                                newBody,
                                {
                                    coercionType: Office.CoercionType.Html,
                                    asyncContext: {}
                                },
                                (asyncResult) => {
                                    if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                                        clientLog("Error in createCampaign-setHtmlBody: " + asyncResult.error.message)
                                    }

                                    console.log("body set", asyncResult);
                                });
                        });
                    }
                }
                else {
                    if (campaignResult.textBody && campaignResult.textBody.length > 0) {
                        item.body.getAsync(Office.CoercionType.Text, (existingBodyResult) => {

                            var exisingBodyValue = (existingBodyResult.value || '') + "\n\n";

                            item.body.setAsync(
                                exisingBodyValue + campaignResult.textBody,
                                {
                                    coercionType: Office.CoercionType.Text,
                                    asyncContext: {}
                                },
                                (asyncResult) => {
                                    if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                                        clientLog("Error in createCampaign-setTextBody: " + asyncResult.error.message)
                                    }
                                });
                        });
                    }
                }
            }

            if (campaignResult.attachments && campaignResult.attachments.length > 0) {

                var baseUrl = window.location.protocol + "//" + window.location.host;
                clientLog("Using base Url: " + baseUrl);
                var attachmentCount = campaignResult.attachments.length;
                campaignResult.attachments.forEach(obj => {
                    if (obj != null) {
                        //baseUrl = "https://b0ba177607d2.ngrok.io"; // Use NGROK for DEV
                        var fullUrl = baseUrl + obj.url;
                        clientLog("Adding attachment: " + fullUrl);

                        var isInline = obj.contentID && obj.contentID.length > 0;
                        var opts = { isInline };
                        item.addFileAttachmentAsync(fullUrl,
                            isInline ? obj.contentID : obj.name,
                            opts,
                            (asyncResult) => {
                                if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                                    clientLog("Error in createCampaign-addAttachment: " + JSON.stringify(asyncResult.error))
                                }
                                attachmentCount--;

                                if (attachmentCount <= 0) {
                                    loadContents();
                                }
                            })
                    }
                });
            }
            else {
                loadContents();
            }





        } else {
            clientLog(`Error in createCampaign: ${campaignResult.reason}`);
            this.setState({ error: campaignResult.reason, working: false })

        }
    }

    cancelCampaign = async () => {
        let campaignResult = await apiDeleteCampaign(this.state.campaign.campaignID);
        if (campaignResult.success) {
            this.setState(this.emptyState(), () => this.loadCampaign(null, null));
            clientLog("Resetting recipient to remove campaign email address");
            await Office.context.mailbox.item.to.setAsync([], null, async () => { });
        }
        else {
            clientLog(`Error in cancelCampaign: ${campaignResult.reason}`);
            this.setState({ error: campaignResult.reason, working: false })

        }
    }

    loadCampaign = async (campaign, newCampaign) => {
        if (campaign != null) {
          
            let {
                files,
                directData
            } = this.state;

            if (files != null) {
                this.setState({ campaign, newCampaign, loaded: true, files: null, directData: null }, () => this.uploadFile(files));
            }
            else if (directData != null) {
                this.setState({ campaign, newCampaign, loaded: true, files: null }, () => this.uploadDirectData(directData));
            }
            else {
                this.setState({ campaign, newCampaign, loaded: true, working: false });
            }
        } else {
            this.setState({ campaign: null, loaded: true, working: false });
        }
    }

    checkCampaignRecipient = async () => {

        // Never let recipient polls overlap themselves or the campaign
        // creation flow; the host allows only a few simultaneous item calls.
        // The age check releases the lock if a host callback never fires, so
        // a lost callback cannot block recipient checks permanently.
        if (this.isUnmounted || this.state.working) {
            return;
        }
        if (this.recipientCheckInFlight) {
            if (Date.now() - this.recipientCheckStartedAt < 30000) {
                return;
            }
            clientLog('Recipient check lock expired without a callback, releasing it');
        }

        let campaign = this.state.campaign;
        if (campaign == null ||
            campaign.campaignToEmailAddress == null ||
            campaign.campaignToEmailAddress.length == 0) {
            return;
        }

        this.recipientCheckInFlight = true;
        this.recipientCheckStartedAt = Date.now();
        try {
            Office.context.mailbox.item.to.getAsync({}, async (result) => {
                let clearInFlightNow = true;
                try {
                    if (result.error) {
                        console.error("Fetching recipients failed", result.error);
                    }

                    const emailParts = campaign.campaignToEmailAddress.split('@');
                    const domain = emailParts[1].toLowerCase();
                    const toRecipients = result.value;
                    let correctCampaignEmailOrNonCampaignEmails = _.filter(toRecipients, toEntry =>
                        toEntry.emailAddress.toLowerCase() === campaign.campaignToEmailAddress.toLowerCase() ||
                        toEntry.emailAddress.toLowerCase().split('@')[1] !== domain);

                    const correctCampaignEmailFound = _.find(toRecipients, toEntry => toEntry.emailAddress.toLowerCase() === campaign.campaignToEmailAddress.toLowerCase());

                    if (!correctCampaignEmailFound) {
                        correctCampaignEmailOrNonCampaignEmails = [
                            {
                                displayName: `MailMerge365 Campaign`,
                                emailAddress: campaign.campaignToEmailAddress,
                            },
                            ...correctCampaignEmailOrNonCampaignEmails];
                    }

                    if (!!toRecipients && !!correctCampaignEmailOrNonCampaignEmails) {
                        if (this.areSameRecipients(toRecipients, correctCampaignEmailOrNonCampaignEmails)) {

                        } else {
                            clientLog("Setting recipients: " + JSON.stringify(correctCampaignEmailOrNonCampaignEmails));

                            clearInFlightNow = false;
                            Office.context.mailbox.item.to.setAsync(correctCampaignEmailOrNonCampaignEmails, null, (setResult) => {
                                this.recipientCheckInFlight = false;
                                if (setResult.error) {
                                    clientLog("Setting recipient failed: " + setResult.error);
                                }
                            });
                        }
                    } else {
                        clientLog("Cannot check recipients - probably dialog open.");
                    }
                } finally {
                    if (clearInFlightNow) {
                        this.recipientCheckInFlight = false;
                    }
                }
            })
        } catch (ex) {
            this.recipientCheckInFlight = false;
            clientLog('Error in checkCampaignRecipient: ' + (ex && ex.message));
        }
    }

    areSameRecipients = (array1, array2) => {
        if (array1.length === array2.length) {
            return array1.every((element, index) => {
                if (element.emailAddress === array2[index].emailAddress) {
                    return true;
                }

                return false;
            });
        }

        return false;
    }


    openWindowWithPost = async (url, data) => {
        var form = document.createElement("form");
        form.target = "_blank";
        form.method = "POST";
        form.action = url;
        form.style.display = "none";

        for (var key in data) {
            var input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = data[key];
            form.appendChild(input);
        }

        //var btn = document.createElement("button");
        //form.appendChild(btn);

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }

    getEmailBody = (mailType: Office.CoercionType): Promise<string> => {
        return new Promise((resolve, reject) => {
            let settled = false;
            try {
                Office.context.mailbox.item.body.getAsync(mailType, {}, (result) => {
                    if (settled) { return; }
                    settled = true;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(new OfficeAsyncError(result.error?.message || 'Failed to get email body', result.error?.code, result.error?.name));
                    }
                });
            } catch (ex) {
                if (!settled) {
                    settled = true;
                    reject(ex);
                }
            }
        });
    }

    getEmailSubject = (): Promise<string> => {
        return new Promise((resolve, reject) => {
            let settled = false;
            try {
                Office.context.mailbox.item.subject.getAsync({}, (result) => {
                    if (settled) { return; }
                    settled = true;
                    if (result.status === Office.AsyncResultStatus.Succeeded) {
                        resolve(result.value);
                    } else {
                        reject(new OfficeAsyncError(result.error?.message || 'Failed to get email subject', result.error?.code, result.error?.name));
                    }
                });
            } catch (ex) {
                if (!settled) {
                    settled = true;
                    reject(ex);
                }
            }
        });
    }

    previewCampaign = async () => {
        const { mailType, campaign } = this.state;
        const maxRetries = 3;
        const retryDelayMs = 500;

        // Check if mailType is available
        if (!mailType) {
            clientLog("previewCampaign: mailType is null, waiting for it to be set...");
            // Wait briefly for mailType to be populated
            await new Promise(resolve => setTimeout(resolve, 500));
            if (!this.state.mailType) {
                clientLog("previewCampaign: mailType still null after waiting");
                this.setState({
                    error: "Unable to determine email format. Please try again in a moment.",
                    errorDismissable: true
                });
                return;
            }
        }

        const currentMailType = this.state.mailType;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                clientLog(`previewCampaign: attempt ${attempt}/${maxRetries}`);

                // Sequential on purpose: the Outlook host allows only a
                // limited number of simultaneous item calls, and parallel
                // calls can fail with error 5100
                const body = await this.getEmailBody(currentMailType);
                const subject = await this.getEmailSubject();

                if (!body && !subject) {
                    throw new Error('Both email body and subject are empty');
                }

                await apiSetCampaignPreview(campaign.campaignID, {
                    subject,
                    email: body,
                    isHtml: (currentMailType === Office.CoercionType.Html)
                });

                clientLog("previewCampaign: preview saved successfully");
                return; // Success - exit the retry loop

            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : String(err);
                clientLog(`previewCampaign: attempt ${attempt} failed - ${errorMessage}`);

                if (attempt < maxRetries) {
                    // Wait before retrying
                    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
                } else {
                    // All retries exhausted - show error to user
                    clientLog(`previewCampaign: all ${maxRetries} attempts failed`);
                    this.setState({
                        error: "Unable to save preview data. Please ensure your email has content and try again.",
                        errorDismissable: true
                    });
                }
            }
        }
    }

    onFileSelected = async (files: FileList, cloneBodyAndSubjectFromCampaignID?: string) => {

        const {
            campaign,
        } = this.state;

        if (campaign == null) {
            // create the campaign first, then upload files
            this.setState({ files, directData: null }, () => this.createCampaign(cloneBodyAndSubjectFromCampaignID));
        } else {
            this.uploadFile(files);
        }
    }

    onDataProvided = async (directData: string, cloneBodyAndSubjectFromCampaignID?: string) => {

        const {
            campaign,
        } = this.state;

        if (campaign == null) {
            // create the campaign first, then upload files
            this.setState({ directData, files: null }, () => this.createCampaign(cloneBodyAndSubjectFromCampaignID));
        } else {
            this.setState({ directData }, () => this.uploadDirectData(directData));
        }
    }

    uploadFile = async (files) => {
        const {
            campaign,
        } = this.state;

        try {
            var result = await apiUploadSourceData(campaign.campaignID, files);
            if (!result.success) {
                clientLog(`Error in uploadFile: ${result.reason}`);

                this.setState({
                    error: "Could not upload file: " + result.reason,
                    errorDismissable: true,
                    working: false,
                })

            } else {
                this.setState({
                    campaign: { ...this.state.campaign, ...result.campaign },
                    working: false,
                })

                await this.checkUploadProgress();
            }
        } catch (ex) {
            clientLog(`Error in uploadFile: ${ex}`);

            this.setState({
                error: "Could not upload file. " + ex,
                errorDismissable: true,
                working: false,
            })
        }
    }

    uploadDirectData = async (data) => {
        const {
            campaign,
        } = this.state;

        try {
            var result = await apiUploadSourceDataDirect(campaign.campaignID, data);
            if (!result.success) {
                clientLog(`Error in uploadDirectData: ${result.reason}`);

                this.setState({
                    error: "Could not upload data: " + result.reason,
                    errorDismissable: true,
                    working: false,
                })

            } else {
                this.setState({
                    campaign: { ...this.state.campaign, ...result.campaign },
                    working: false,
                })

                await this.checkUploadProgress();
            }
        } catch (ex) {
            clientLog(`Error in uploadDirectData: ${ex}`);

            this.setState({
                error: "Could not upload data. " + ex,
                errorDismissable: true,
                working: false,
            })
        }
    }

    checkUploadProgress = async () => {

        const {
            campaign,
            files,
        } = this.state;

        if (campaign == null) return; // cancelled 

        var result = await apiCheckSourceData(campaign.campaignID);
        if (!result.success) {
            clientLog(`Error in onFileUpload: ${result.reason}`);
            if (result.canContinue) {
                this.setState({
                    warning: result.reason,
                    warningCancelAction: () => this.setState({
                        warning: null,
                        campaign: { ...this.state.campaign, sourceDataRows: null }
                    }),
                    warningContinueAction: async () => {
                        await apiContinueSourceData(campaign.campaignID);
                        this.setState({ warning: null }, () => {
                            setTimeout(this.checkUploadProgress, 2000);
                        });
                    }
                })
            } else {
                this.setState({
                    error: "Could not upload file: " + result.reason,
                    errorDismissable: true,
                    campaign: {
                        ...this.state.campaign,
                        sourceDataUploadedOn: null,
                        sourceDataRows: null
                    },
                })
            }


        } else {

            clientLog("Waiting ... " + result.sourceProcessedRows + "/" + result.sourceDataRows + " rows");

            if (!result.complete) {
                this.setState({
                    campaign: { ...this.state.campaign, ...result },
                })

                setTimeout(this.checkUploadProgress, 2000);
            } else {
                clientLog("Upload complete.")

                this.setState({
                    loaded: true,
                    campaign: {
                        ...this.state.campaign,
                        ...result
                    }
                })
            }
        }
    }

    onFileClick = (event) => {
        event.target.value = null
    }

    onAddTemplateToken = (tokenKey: string, toSubject: boolean) => {
        const {
            mailType
        } = this.state;

        const item = Office.context.mailbox.item;
        const tokenValue = `{{${tokenKey}}}`

        const addToSubject = () => {
            item.subject.getAsync((subjectResult) => {
                item.subject.setAsync(subjectResult.value + tokenValue);
            })
        }

        const addToBody = () => {
            if (mailType == Office.CoercionType.Html) {
                item.body.setSelectedDataAsync(
                    tokenValue,
                    {
                        coercionType: Office.CoercionType.Html,
                        asyncContext: {}
                    },
                    (asyncResult) => {
                        if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                            clientLog("Error in onAddTemplateToken: " + asyncResult.error.message)
                        }
                    });
            }
            else {
                item.body.setSelectedDataAsync(
                    tokenValue,
                    {
                        coercionType: Office.CoercionType.Text,
                        asyncContext: {}
                    },
                    (asyncResult) => {
                        if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                            clientLog("Error in onAddTemplateToken: " + asyncResult.error.message)
                        }
                    });
            }
        }

        if (toSubject) {
            addToSubject();
        } else {
            addToBody();
        }
        //Waiting for bug fix from Microsoft
        //var isSubjectSelected = false;
        //item.getSelectedDataAsync(mailType, (asyncResult) => {
        //    isSubjectSelected = asyncResult.value && asyncResult.value.sourceProperty == "subject";

        //    if (isSubjectSelected) {
        //        addToSubject();
        //    } else {
        //        addToBody();
        //    }
        //});



    }

    onAddUnsubscribeLink = () => {
        const {
            mailType
        } = this.state;

        const item = Office.context.mailbox.item;
        const tokenValue = `{{unsubscribelink}}`
        if (mailType == Office.CoercionType.Html) {
            item.body.setSelectedDataAsync(
                `<small>If you prefer not to receive any further emails from me, please click <a href="${tokenValue}">here</a>.</small>`,
                {
                    coercionType: Office.CoercionType.Html,
                    asyncContext: {}
                },
                (asyncResult) => {
                    if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                        clientLog("Error in onAddUnsubscribeLink: " + asyncResult.error.message)
                    }
                });
        }
        else {
            item.body.setSelectedDataAsync(
                `If you prefer not to receive any further emails from me, please click this link:\n${tokenValue}`,
                {
                    coercionType: Office.CoercionType.Text,
                    asyncContext: {}
                },
                (asyncResult) => {
                    if (asyncResult.status == Office.AsyncResultStatus.Failed) {
                        clientLog("Error in onAddUnsubscribeLink: " + asyncResult.error.message)
                    }
                });
        }
    }

    onSetSettings = async (settings: {
        readTrackingEnabled?: boolean,
        linkTrackingEnabled?: boolean,
        unsubscribeEnabled?: boolean,
        name?: string,
        bcc?: string,
        replyTo?: string,
        delayInSecondsForEachCampaignMail?: number,
    }) => {
        const {
            campaign
        } = this.state;

        var result = await apiSetCampaignSettings(campaign.campaignID, settings);

        if (!result.success) {
            clientLog(`Error in onSetSettings: ${result.reason}`);
            this.setState({ error: result.reason });
        } else {
            var newCampaign = {
                ...campaign,
                readTrackingEnabled: settings.readTrackingEnabled == null ? campaign.readTrackingEnabled : settings.readTrackingEnabled,
                linkTrackingEnabled: settings.linkTrackingEnabled == null ? campaign.linkTrackingEnabled : settings.linkTrackingEnabled,
                unsubscribeEnabled: settings.unsubscribeEnabled == null ? campaign.unsubscribeEnabled : settings.unsubscribeEnabled,
                name: settings.name == null ? campaign.name : settings.name,
                bcc: settings.bcc == null ? campaign.bcc : settings.bcc,
                replyTo: settings.replyTo == null ? campaign.replyTo : settings.replyTo,
                delayInSecondsForEachCampaignMail: settings.delayInSecondsForEachCampaignMail == null ? campaign.delayInSecondsForEachCampaignMail : settings.delayInSecondsForEachCampaignMail,
            };

            this.setState({
                campaign: newCampaign
            })
        }
    }

    onReadTrackingEnabledChanged = async (value: boolean) => await this.onSetSettings({ readTrackingEnabled: value });

    onLinkTrackingEnabledChanged = async (value: boolean) => await this.onSetSettings({ linkTrackingEnabled: value });

    onUnsubscribeEnabledChanged = async (value: boolean) => await this.onSetSettings({ unsubscribeEnabled: value });

    onNameChanged = async (value: string) => await this.onSetSettings({ name: value });

    onBccChanged = async (value: string) => await this.onSetSettings({ bcc: value });

    onReplyToChanged = async (value: string) => await this.onSetSettings({ replyTo: value });

    onDelayChanged = async (value: number) => await this.onSetSettings({ delayInSecondsForEachCampaignMail: value });

    render() {
        const {
            title,
            account,
            onSignout,
            firstRun,
            autoLogin,
        } = this.props;

        const {
            hasSentPreviousCampaign,
            error,
            errorAdditional,
            errorDismissable,
            warning,
            warningCancelAction,
            warningContinueAction,
            campaign,
            loaded,
            newCampaign,
            attachmentWarning,
            working,
            directData,
        } = this.state;

        if (error) {
            return <Message
                title="Error"
                message={error}
                additional={errorAdditional}
                dismissButtonTitle="OK"
                onDismiss={() => this.setState({ error: null, errorAdditional: null, errorDismissable: false })}
            />
        }


        if (warning) {
            return <Message
                title="Warning"
                message={warning}
                cancelButtonTitle="Cancel"
                continueButtonTitle="Ignore and continue"
                onCancel={warningCancelAction}
                onContinue={warningContinueAction}
            />
        }

        if (working) {
            return <Progress
                title={'Creating your campaign'}
                logo='assets/logo-filled.png'
                message='Please wait a moment...'
            />
        }

        if (loaded) {

            const campaignCreated = campaign != null;
            if (campaignCreated && campaign.sourceDataUploadedOn != null) {
                return <CampaignView
                    onSignout={onSignout}
                    account={account}
                    autoLogin={autoLogin}
                    showIntro={!hasSentPreviousCampaign}
                    campaign={campaign}
                    fileDownloadUrl=""
                    filename={campaign.sourceDataFilename}
                    fileRows={campaign.sourceDataRows}
                    fileUploaded={moment.utc(campaign.sourceDataUploadedOn)}
                    onFileUpload={this.onFileSelected}
                    columns={(campaign.sourceDataKeys || '').split('\n')}
                    onAddTemplateToken={this.onAddTemplateToken}
                    onAddUnsubscribeLink={this.onAddUnsubscribeLink}
                    readTrackingEnabled={campaign.readTrackingEnabled}
                    linkTrackingEnabled={campaign.linkTrackingEnabled}
                    unsubscribeEnabled={campaign.unsubscribeEnabled}
                    onReadTrackingEnabledChanged={this.onReadTrackingEnabledChanged}
                    onLinkTrackingEnabledChanged={this.onLinkTrackingEnabledChanged}
                    onUnsubscribeEnabledChanged={this.onUnsubscribeEnabledChanged}
                    onNameChanged={this.onNameChanged}
                    onBccChanged={this.onBccChanged}
                    onReplyToChanged={this.onReplyToChanged}
                    onDelayChanged={this.onDelayChanged}
                    onCancelCampaign={this.cancelCampaign}
                    onPreview={this.previewCampaign}
                    attachmentWarning={attachmentWarning}
                />
            } else {
                return <CampaignStart
                    onSignout={() => {
                        apiUnlink();
                        onSignout();
                    }}
                    firstRun={firstRun}
                    account={account}
                    autoLogin={autoLogin}
                    defaultDirectData={directData}
                    onFileUpload={this.onFileSelected}
                    onDirectDataUpload={this.onDataProvided}
                    onCreateFollowUp={(createSourceFromCampaignID, createSourceFromCriteria) => this.createCampaign(null, createSourceFromCampaignID, createSourceFromCriteria)}
                />
            }

        } else {
            return <Progress
                title={title}
                logo='assets/logo-filled.png'
                message='Checking campaigns...'
            />
        }

        //return (
        //    <div className='ms-welcome'>
        //        <p>CampaignID: {campaignID}</p>
        //        {campaignID != null && <input
        //            type="file"
        //            id="files"
        //            onChange={this.onFileUpload}
        //            onClick={this.onFileClick}
        //        />}
        //        <Button
        //            className='ms-welcome__action'
        //            buttonType={ButtonType.hero}
        //            onClick={this.onCreateCampaign}>Start campaign</Button>
        //    </div>
        //);
    }
}
